/**
 * ============================================================================
 * USER / TENANT PAGE: Dynamic Image Templates & Studio Hub
 * ============================================================================
 * Purpose:
 *   Displays saved dynamic image templates with rich visual previews,
 *   variable inspection, live testing modals, and campaign launch triggers.
 * ============================================================================
 */

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import dynamicImageService from "../../services/dynamicImageService";
import {
  PlusIcon,
  SparklesIcon,
  PencilSquareIcon,
  TrashIcon,
  EyeIcon,
  DocumentDuplicateIcon,
  PaperAirplaneIcon,
  MagnifyingGlassIcon,
  Squares2X2Icon,
  ListBulletIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { generateDataUrl, resolveImageUrl } from "./canvasRenderer";

// Default Starter Templates for instant inspiration
const STARTER_TEMPLATES = [
  {
    title: "Festival Greeting Card",
    category: "Festival & Holiday",
    canvas: {
      width: 1080,
      height: 1080,
      backgroundColor: "#4c0519",
      backgroundImage: "",
    },
    layers: [
      {
        id: "starter_f1",
        type: "text",
        name: "Festive Title",
        text: "✨ Happy Festive Season! ✨",
        x: 100,
        y: 180,
        fontSize: 54,
        fontFamily: "Outfit",
        fontWeight: 800,
        color: "#fbbf24",
        textAlign: "center",
        maxWidth: 880,
        lineHeight: 70,
        shadowColor: "rgba(251,191,36,0.5)",
        shadowBlur: 14,
        visible: true,
      },
      {
        id: "starter_f2",
        type: "text",
        name: "Personalized Greeting",
        text: "Warmest wishes to {{name}} & Family",
        x: 100,
        y: 340,
        fontSize: 48,
        fontFamily: "Poppins",
        fontWeight: 700,
        color: "#ffffff",
        textAlign: "center",
        maxWidth: 880,
        lineHeight: 64,
        visible: true,
      },
      {
        id: "starter_f3",
        type: "text",
        name: "Special Blessing",
        text: "May this season bring endless joy, success, and prosperity to you.\n- From Digiforest Team",
        x: 140,
        y: 500,
        fontSize: 32,
        fontFamily: "Inter",
        fontWeight: 400,
        color: "#fecdd3",
        textAlign: "center",
        maxWidth: 800,
        lineHeight: 48,
        visible: true,
      },
    ],
  },
  {
    title: "VIP Flash Sale Offer",
    category: "Promotions",
    canvas: {
      width: 1080,
      height: 1080,
      backgroundColor: "#0f172a",
      backgroundImage: "",
    },
    layers: [
      {
        id: "starter_s1",
        type: "text",
        name: "Sale Header",
        text: "EXCLUSIVE VIP PASS",
        x: 100,
        y: 160,
        fontSize: 40,
        fontFamily: "Oswald",
        fontWeight: 700,
        color: "#38bdf8",
        textAlign: "center",
        maxWidth: 880,
        lineHeight: 50,
        visible: true,
      },
      {
        id: "starter_s2",
        type: "text",
        name: "Recipient Offer",
        text: "Hey {{name}}, You Got {{discount}} OFF!",
        x: 80,
        y: 280,
        fontSize: 62,
        fontFamily: "Outfit",
        fontWeight: 800,
        color: "#ffffff",
        textAlign: "center",
        maxWidth: 920,
        lineHeight: 80,
        shadowColor: "rgba(0,0,0,0.5)",
        shadowBlur: 12,
        visible: true,
      },
      {
        id: "starter_s3",
        type: "text",
        name: "Coupon Code",
        text: "Use Promo Code: {{code}}",
        x: 100,
        y: 520,
        fontSize: 44,
        fontFamily: "Roboto",
        fontWeight: 700,
        color: "#facc15",
        textAlign: "center",
        maxWidth: 880,
        lineHeight: 56,
        visible: true,
      },
    ],
  },
  {
    title: "Royal Luxury Certificate & Wave",
    category: "Marketing",
    canvas: {
      width: 1080,
      height: 1080,
      backgroundColor: "#0b0f19",
      backgroundImage: "",
      padding: 30,
      paddingTop: 30,
      paddingRight: 30,
      paddingBottom: 30,
      paddingLeft: 30,
      frame: {
        type: "LUXURY_GOLD_WAVE",
        position: "INSIDE",
        width: 28,
        scaleX: 100,
        scaleY: 100,
        offsetX: 0,
        offsetY: 0,
        color: "#d4af37",
        accentColor: "#f59e0b",
        customUrl: "/frames/luxury_gold_wave.png",
      },
    },
    layers: [
      {
        id: "starter_rw1",
        type: "text",
        name: "Award Title",
        text: "CERTIFICATE OF EXCELLENCE",
        x: 120,
        y: 280,
        fontSize: 44,
        fontFamily: "Playfair Display",
        fontWeight: 700,
        color: "#d4af37",
        textAlign: "center",
        maxWidth: 840,
        lineHeight: 55,
        visible: true,
      },
      {
        id: "starter_rw2",
        type: "text",
        name: "Honoree Name",
        text: "{{name}}",
        x: 100,
        y: 430,
        fontSize: 68,
        fontFamily: "Outfit",
        fontWeight: 800,
        color: "#ffffff",
        textAlign: "center",
        maxWidth: 880,
        lineHeight: 80,
        visible: true,
      },
      {
        id: "starter_rw3",
        type: "text",
        name: "Citation Text",
        text: "In recognition of your exceptional achievement & valued partnership.\nCode: {{code}}",
        x: 140,
        y: 590,
        fontSize: 30,
        fontFamily: "Inter",
        fontWeight: 400,
        color: "#94a3b8",
        textAlign: "center",
        maxWidth: 800,
        lineHeight: 46,
        visible: true,
      },
    ],
  },
  {
    title: "Polaroid Photo Memories Collage",
    category: "Festival & Holiday",
    canvas: {
      width: 1080,
      height: 1080,
      backgroundColor: "#18181b",
      backgroundImage: "",
      paddingTop: 24,
      paddingRight: 24,
      paddingBottom: 140,
      paddingLeft: 24,
      isPaddingLinked: false,
      frame: {
        type: "POLAROID_TRIO",
        position: "INSIDE",
        width: 24,
        scaleX: 100,
        scaleY: 100,
        offsetX: 0,
        offsetY: 0,
        customUrl: "/frames/polaroid_tilted_trio.png",
      },
    },
    layers: [
      {
        id: "starter_pol1",
        type: "text",
        name: "Memory Caption",
        text: "Cherished Moments with {{name}}",
        x: 80,
        y: 950,
        fontSize: 42,
        fontFamily: "Pacifico",
        fontWeight: 600,
        color: "#ffffff",
        textAlign: "center",
        maxWidth: 920,
        lineHeight: 52,
        visible: true,
      },
    ],
  },
];

const CATEGORIES = [
  "All",
  "Marketing",
  "Festival & Holiday",
  "Promotions",
  "Utility",
  "General",
];

const DynamicImageTemplates = () => {
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [viewMode, setViewMode] = useState("grid"); // "grid" | "table"

  // Quick Test Modal state
  const [testingTemplate, setTestingTemplate] = useState(null);
  const [testValues, setTestValues] = useState({});
  const [renderedPreview, setRenderedPreview] = useState("");
  const [renderingPreview, setRenderingPreview] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const res = await dynamicImageService.getTemplates();
      const list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res)
        ? res
        : [];
      setTemplates(list);
    } catch (err) {
      console.error("Failed to fetch dynamic templates:", err);
      toast.error("Failed to load dynamic image templates");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    const result = await Swal.fire({
      title: "Delete Dynamic Template?",
      text: `Are you sure you want to delete "${name || "this template"}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete it",
    });

    if (result.isConfirmed) {
      try {
        await dynamicImageService.deleteTemplate(id);
        toast.success("Template deleted successfully");
        setTemplates((prev) => prev.filter((t) => t._id !== id));
      } catch (err) {
        console.error(err);
        toast.error("Failed to delete template");
      }
    }
  };

  const handleDuplicate = async (tmpl) => {
    try {
      const copyPayload = {
        str_template_name: `${tmpl.str_title || tmpl.str_template_name || "Template"} (Copy)`,
        str_title: `${tmpl.str_title || tmpl.str_template_name || "Template"} (Copy)`,
        str_category: tmpl.str_category || "Marketing",
        canvas: tmpl.canvas || {
          width: tmpl.num_width || 1080,
          height: tmpl.num_height || 1080,
          backgroundColor: tmpl.str_bg_color || "#1e293b",
          backgroundImage: tmpl.str_bg_image_url || "",
        },
        layers: tmpl.layers || [],
        arr_variables: tmpl.arr_variables || [],
        preview_thumbnail: tmpl.preview_thumbnail || tmpl.str_preview_image_url || "",
      };

      const res = await dynamicImageService.createTemplate(copyPayload);
      if (res?.success || res?.data?.success) {
        toast.success("Template duplicated!");
        fetchTemplates();
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to duplicate template");
    }
  };

  const handleUseStarterTemplate = async (starter) => {
    try {
      const res = await dynamicImageService.createTemplate({
        str_template_name: starter.title,
        str_title: starter.title,
        str_category: starter.category,
        canvas: starter.canvas,
        layers: starter.layers,
      });
      const createdId = res?.data?._id || res?.data?.data?._id || res?._id;
      if ((res?.success || res?.data?.success) && createdId) {
        toast.success("Starter template ready!");
        navigate(`/workspace/dynamic-images/edit/${createdId}`);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to create starter template");
    }
  };

  const handleOpenTestModal = async (tmpl) => {
    setTestingTemplate(tmpl);
    const initialVals = {};
    const vars = tmpl.arr_variables || [];
    vars.forEach((v) => {
      initialVals[v] = v === "name" ? "Alex Morgan" : v === "offer" || v === "discount" ? "40% OFF" : v === "code" ? "SPECIAL40" : `Sample ${v}`;
    });
    setTestValues(initialVals);

    setRenderingPreview(true);
    try {
      const dataUrl = await generateDataUrl(
        {
          canvas: tmpl.canvas || {
            width: tmpl.num_width || 1080,
            height: tmpl.num_height || 1080,
            backgroundColor: tmpl.str_bg_color || "#1e293b",
            backgroundImage: tmpl.str_bg_image_url || "",
          },
          layers: tmpl.layers || [],
        },
        initialVals
      );
      setRenderedPreview(dataUrl);
    } catch (e) {
      console.error(e);
    } finally {
      setRenderingPreview(false);
    }
  };

  const handleUpdateTestVal = async (key, val) => {
    const updated = { ...testValues, [key]: val };
    setTestValues(updated);

    if (!testingTemplate) return;
    try {
      const dataUrl = await generateDataUrl(
        {
          canvas: testingTemplate.canvas || {
            width: testingTemplate.num_width || 1080,
            height: testingTemplate.num_height || 1080,
            backgroundColor: testingTemplate.str_bg_color || "#1e293b",
            backgroundImage: testingTemplate.str_bg_image_url || "",
          },
          layers: testingTemplate.layers || [],
        },
        updated
      );
      setRenderedPreview(dataUrl);
    } catch (e) {
      console.error(e);
    }
  };

  // Filtered Templates
  const filteredTemplates = useMemo(() => {
    return templates.filter((t) => {
      const name = (t.str_title || t.str_template_name || "").toLowerCase();
      const cat = t.str_category || "Marketing";
      const matchesSearch = name.includes(searchTerm.toLowerCase());
      const matchesCategory =
        selectedCategory === "All" || cat === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [templates, searchTerm, selectedCategory]);

  return (
    <div className="min-h-screen bg-[#f5f6fa] dark:bg-[#0f172a] space-y-6">
      {/* Top Header & Action Buttons */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white dark:bg-[#151824] p-5 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-xs">
        <div>
          <div className="flex items-center gap-2.5">
            <span className="p-2 rounded-xl bg-[#3a57e8]/10 text-[#3a57e8]">
              <SparklesIcon className="w-6 h-6" />
            </span>
            <div>
              <h1 className="text-xl font-bold text-[#232d42] dark:text-white">
                Dynamic Image Studio & Templates
              </h1>
              <p className="text-xs text-[#8a92a6] dark:text-gray-400 mt-0.5">
                Create personalized dynamic image templates with {"{{variables}}"} for WhatsApp campaigns
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2.5 w-full sm:w-auto">
          <button
            onClick={() => navigate("/workspace/image-camp")}
            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-600 hover:text-white border border-emerald-500/20 text-xs font-semibold transition shadow-xs"
          >
            <PaperAirplaneIcon className="w-4 h-4" />
            <span>Image Camp</span>
          </button>

          <button
            onClick={() => navigate("/workspace/dynamic-images/create")}
            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#3a57e8] hover:bg-blue-600 text-white text-xs font-semibold transition shadow-md shadow-[#3a57e8]/25"
          >
            <PlusIcon className="w-4 h-4 stroke-2" />
            <span>Create Template</span>
          </button>
        </div>
      </div>

      {/* Filter Bar & Controls */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-white dark:bg-[#151824] p-4 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-xs">
        {/* Search */}
        <div className="relative w-full md:w-80">
          <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search templates..."
            className="w-full pl-9 pr-4 py-2 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-xs text-gray-800 dark:text-gray-200 focus:outline-none focus:border-[#3a57e8]"
          />
        </div>

        {/* Category Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto w-full md:w-auto pb-1 md:pb-0">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition ${
                selectedCategory === cat
                  ? "bg-[#3a57e8] text-white shadow-xs"
                  : "bg-gray-100 dark:bg-gray-800/80 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* View Switcher & Refresh */}
        <div className="flex items-center gap-2 self-end md:self-auto">
          <div className="flex items-center bg-gray-100 dark:bg-gray-800 p-1 rounded-xl">
            <button
              onClick={() => setViewMode("grid")}
              className={`p-1.5 rounded-lg transition ${
                viewMode === "grid"
                  ? "bg-white dark:bg-gray-700 text-[#3a57e8] shadow-xs"
                  : "text-gray-400 hover:text-gray-600"
              }`}
              title="Grid View"
            >
              <Squares2X2Icon className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode("table")}
              className={`p-1.5 rounded-lg transition ${
                viewMode === "table"
                  ? "bg-white dark:bg-gray-700 text-[#3a57e8] shadow-xs"
                  : "text-gray-400 hover:text-gray-600"
              }`}
              title="Table View"
            >
              <ListBulletIcon className="w-4 h-4" />
            </button>
          </div>

          <button
            onClick={fetchTemplates}
            className="p-2 rounded-xl border border-gray-200 dark:border-gray-800 text-gray-500 hover:text-[#3a57e8] hover:bg-gray-50 dark:hover:bg-gray-800 transition"
            title="Refresh"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Content State */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-8 h-8 border-3 border-[#3a57e8] border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-3 text-xs text-gray-500">Loading dynamic image templates...</p>
        </div>
      ) : filteredTemplates.length === 0 ? (
        /* Empty State with Starter Templates */
        <div className="space-y-6">
          <div className="bg-white dark:bg-[#151824] rounded-2xl border border-dashed border-gray-300 dark:border-gray-800 p-8 text-center max-w-xl mx-auto">
            <span className="text-4xl inline-block mb-3">🎨</span>
            <h3 className="text-base font-bold text-[#232d42] dark:text-white">
              {searchTerm ? "No matching templates found" : "No Dynamic Templates Yet"}
            </h3>
            <p className="text-xs text-[#8a92a6] dark:text-gray-400 mt-1 max-w-md mx-auto">
              {searchTerm
                ? "Try searching for a different keyword or clear your filters."
                : "Create high-converting dynamic image templates with personalized customer names, discounts, and promo codes."}
            </p>
            <div className="mt-5">
              <button
                onClick={() => navigate("/workspace/dynamic-images/create")}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#3a57e8] text-white text-xs font-semibold shadow-md shadow-[#3a57e8]/30 hover:bg-blue-600 transition"
              >
                <PlusIcon className="w-4 h-4" />
                <span>Create First Template</span>
              </button>
            </div>
          </div>

          {/* Starter Inspiration Templates */}
          <div>
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 px-1">
              Or Start with a Ready-Made Template
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {STARTER_TEMPLATES.map((starter) => (
                <div
                  key={starter.title}
                  className="bg-white dark:bg-[#151824] rounded-2xl border border-gray-100 dark:border-gray-800 p-5 flex flex-col justify-between hover:shadow-md transition"
                >
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-md bg-blue-500/10 text-blue-500 font-mono">
                        {starter.category}
                      </span>
                      <span className="text-[11px] text-gray-400">
                        {starter.canvas.width} × {starter.canvas.height}px
                      </span>
                    </div>
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white">
                      {starter.title}
                    </h4>
                    <p className="text-xs text-gray-500 mt-1">
                      Includes dynamic placeholders:{" "}
                      <span className="font-mono text-blue-500">
                        {starter.layers
                          .map((l) => l.text)
                          .join(" ")
                          .match(/\{\{([a-zA-Z0-9_-]+)\}\}/g)
                          ?.join(", ") || "{{name}}"}
                      </span>
                    </p>
                  </div>
                  <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-800 flex justify-end">
                    <button
                      onClick={() => handleUseStarterTemplate(starter)}
                      className="px-4 py-1.5 rounded-lg bg-gray-900 dark:bg-gray-800 text-white hover:bg-[#3a57e8] text-xs font-medium transition flex items-center gap-1.5"
                    >
                      <span>Use Template</span>
                      <span>→</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : viewMode === "grid" ? (
        /* GRID VIEW */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filteredTemplates.map((tmpl) => {
            const previewUrl =
              tmpl.preview_thumbnail ||
              tmpl.str_preview_image_url ||
              tmpl.canvas?.backgroundImage ||
              tmpl.str_bg_image_url ||
              "";
            const width = tmpl.canvas?.width || tmpl.num_width || 1080;
            const height = tmpl.canvas?.height || tmpl.num_height || 1080;
            const vars = tmpl.arr_variables || [];

            return (
              <div
                key={tmpl._id}
                className="bg-white dark:bg-[#151824] rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden shadow-xs hover:shadow-lg transition-all duration-200 flex flex-col group"
              >
                {/* Thumbnail Preview Area */}
                <div
                  className="relative aspect-square w-full bg-slate-900 flex items-center justify-center overflow-hidden cursor-pointer"
                  onClick={() => navigate(`/workspace/dynamic-images/edit/${tmpl._id}`)}
                >
                  {previewUrl ? (
                    <img
                      src={resolveImageUrl(previewUrl)}
                      alt={tmpl.str_title || tmpl.str_template_name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        const cur = e.target.src || "";
                        if (cur.includes("apis.digiforest.in")) {
                          e.target.src = cur.replace("https://apis.digiforest.in", "http://localhost:8005");
                        } else {
                          e.target.style.display = "none";
                        }
                      }}
                    />
                  ) : (
                    <div
                      className="w-full h-full flex flex-col items-center justify-center p-4 text-center"
                      style={{
                        backgroundColor:
                          tmpl.canvas?.backgroundColor || tmpl.str_bg_color || "#1e293b",
                      }}
                    >
                      <span className="text-3xl mb-1">🖼️</span>
                      <span className="text-xs font-semibold text-white/90 truncate max-w-[85%]">
                        {tmpl.str_title || tmpl.str_template_name}
                      </span>
                    </div>
                  )}

                  {/* Top Badges */}
                  <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-black/60 backdrop-blur text-white font-mono">
                      {width} × {height}
                    </span>
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#3a57e8]/80 backdrop-blur text-white">
                      {tmpl.str_category || "Marketing"}
                    </span>
                  </div>

                  {/* Quick Action Overlay on Hover */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenTestModal(tmpl);
                      }}
                      className="p-2 rounded-xl bg-white text-gray-800 hover:bg-emerald-500 hover:text-white transition shadow"
                      title="Test Dynamic Variables"
                    >
                      <EyeIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/workspace/dynamic-images/edit/${tmpl._id}`);
                      }}
                      className="p-2 rounded-xl bg-white text-gray-800 hover:bg-[#3a57e8] hover:text-white transition shadow"
                      title="Edit in Studio"
                    >
                      <PencilSquareIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicate(tmpl);
                      }}
                      className="p-2 rounded-xl bg-white text-gray-800 hover:bg-blue-600 hover:text-white transition shadow"
                      title="Duplicate"
                    >
                      <DocumentDuplicateIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-4 flex-1 flex flex-col justify-between">
                  <div>
                    <h3
                      className="text-sm font-bold text-[#232d42] dark:text-white truncate cursor-pointer hover:text-[#3a57e8] transition"
                      onClick={() => navigate(`/workspace/dynamic-images/edit/${tmpl._id}`)}
                    >
                      {tmpl.str_title || tmpl.str_template_name || "Dynamic Template"}
                    </h3>

                    {/* Dynamic Variables Chips */}
                    <div className="mt-2.5 flex flex-wrap gap-1">
                      {vars.length > 0 ? (
                        vars.map((v) => (
                          <span
                            key={v}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-mono font-medium"
                          >
                            {"{{"} {v} {"}}"}
                          </span>
                        ))
                      ) : (
                        <span className="text-[11px] text-gray-400 italic">Static Template</span>
                      )}
                    </div>
                  </div>

                  {/* Card Bottom Actions */}
                  <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
                    <button
                      onClick={() =>
                        navigate(`/workspace/image-camp/create?templateId=${tmpl._id}`)
                      }
                      className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400 hover:underline"
                    >
                      <PaperAirplaneIcon className="w-3.5 h-3.5" />
                      <span>Send Campaign</span>
                    </button>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleDelete(tmpl._id, tmpl.str_title || tmpl.str_template_name)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
                        title="Delete"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* TABLE VIEW */
        <div className="bg-white dark:bg-[#151824] rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-gray-50 dark:bg-gray-900/60 text-gray-400 uppercase tracking-wider font-semibold border-b border-gray-100 dark:border-gray-800">
                <tr>
                  <th className="px-5 py-3.5">Template</th>
                  <th className="px-4 py-3.5">Category</th>
                  <th className="px-4 py-3.5">Size</th>
                  <th className="px-4 py-3.5">Variables</th>
                  <th className="px-4 py-3.5">Created</th>
                  <th className="px-5 py-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {filteredTemplates.map((tmpl) => {
                  const previewUrl =
                    tmpl.preview_thumbnail ||
                    tmpl.str_preview_image_url ||
                    tmpl.canvas?.backgroundImage ||
                    tmpl.str_bg_image_url ||
                    "";
                  const vars = tmpl.arr_variables || [];
                  return (
                    <tr
                      key={tmpl._id}
                      className="hover:bg-gray-50/60 dark:hover:bg-gray-800/40 transition"
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg overflow-hidden bg-slate-900 flex-shrink-0 border border-gray-200 dark:border-gray-700">
                            {previewUrl ? (
                              <img
                                src={resolveImageUrl(previewUrl)}
                                alt="Thumb"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  const cur = e.target.src || "";
                                  if (cur.includes("apis.digiforest.in")) {
                                    e.target.src = cur.replace("https://apis.digiforest.in", "http://localhost:8005");
                                  } else {
                                    e.target.style.display = "none";
                                  }
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-xs">
                                🖼️
                              </div>
                            )}
                          </div>
                          <div>
                            <div
                              onClick={() =>
                                navigate(`/workspace/dynamic-images/edit/${tmpl._id}`)
                              }
                              className="font-bold text-gray-900 dark:text-white cursor-pointer hover:text-[#3a57e8]"
                            >
                              {tmpl.str_title || tmpl.str_template_name}
                            </div>
                            <div className="text-[11px] text-gray-400">
                              ID: {tmpl._id?.slice(-6)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                          {tmpl.str_category || "Marketing"}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-gray-600 dark:text-gray-400">
                        {tmpl.canvas?.width || tmpl.num_width || 1080} ×{" "}
                        {tmpl.canvas?.height || tmpl.num_height || 1080}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {vars.map((v) => (
                            <span
                              key={v}
                              className="text-[10px] px-1.5 py-0.2 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-mono"
                            >
                              {"{{"} {v} {"}}"}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-[11px]">
                        {tmpl.dttm_created_on
                          ? new Date(tmpl.dttm_created_on).toLocaleDateString()
                          : "-"}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => handleOpenTestModal(tmpl)}
                            className="p-1.5 text-gray-400 hover:text-emerald-500 rounded-lg"
                            title="Test"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() =>
                              navigate(`/workspace/image-camp/create?templateId=${tmpl._id}`)
                            }
                            className="p-1.5 text-gray-400 hover:text-blue-500 rounded-lg"
                            title="Launch Campaign"
                          >
                            <PaperAirplaneIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() =>
                              navigate(`/workspace/dynamic-images/edit/${tmpl._id}`)
                            }
                            className="p-1.5 text-gray-400 hover:text-[#3a57e8] rounded-lg"
                            title="Edit"
                          >
                            <PencilSquareIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() =>
                              handleDelete(tmpl._id, tmpl.str_title || tmpl.str_template_name)
                            }
                            className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg"
                            title="Delete"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* QUICK LIVE VARIABLE TEST MODAL */}
      {testingTemplate && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#1e293b] border border-slate-700 rounded-2xl w-full max-w-3xl overflow-hidden flex flex-col shadow-2xl">
            <div className="p-4 border-b border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xl">⚡</span>
                <h3 className="text-sm font-bold text-white">
                  Live Variable Preview: {testingTemplate.str_title || testingTemplate.str_template_name}
                </h3>
              </div>
              <button
                onClick={() => setTestingTemplate(null)}
                className="text-slate-400 hover:text-white p-1 text-sm"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 p-6 overflow-y-auto max-h-[75vh]">
              {/* Inputs */}
              <div className="space-y-3">
                <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                  Sample Recipient Variables
                </h4>
                {(testingTemplate.arr_variables || []).length === 0 ? (
                  <p className="text-xs text-slate-500 italic">
                    This template does not contain dynamic placeholders.
                  </p>
                ) : (
                  (testingTemplate.arr_variables || []).map((v) => (
                    <div key={v}>
                      <label className="text-xs text-blue-400 font-mono block mb-1">
                        {"{{"} {v} {"}}"}
                      </label>
                      <input
                        type="text"
                        value={testValues[v] || ""}
                        onChange={(e) => handleUpdateTestVal(v, e.target.value)}
                        className="w-full text-xs bg-slate-900 border border-slate-700 rounded-lg p-2 text-white focus:border-[#3a57e8] focus:outline-none"
                      />
                    </div>
                  ))
                )}
              </div>

              {/* Live Render */}
              <div className="flex flex-col items-center justify-center">
                <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 self-start">
                  Personalized Render Output
                </h4>
                {renderingPreview ? (
                  <div className="py-12 text-xs text-slate-400">Rendering preview...</div>
                ) : renderedPreview ? (
                  <div className="rounded-xl overflow-hidden border border-slate-700 shadow-xl bg-slate-900 max-w-xs">
                    <img src={renderedPreview} alt="Preview" className="w-full h-auto" />
                  </div>
                ) : (
                  <div className="text-xs text-slate-500">No preview generated</div>
                )}
              </div>
            </div>

            <div className="p-4 border-t border-slate-800 flex justify-end gap-2 bg-slate-900/60">
              <button
                onClick={() => {
                  const id = testingTemplate._id;
                  setTestingTemplate(null);
                  navigate(`/workspace/image-camp/create?templateId=${id}`);
                }}
                className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold flex items-center gap-1.5"
              >
                <PaperAirplaneIcon className="w-3.5 h-3.5" />
                <span>Launch Campaign with This Template</span>
              </button>
              <button
                onClick={() => setTestingTemplate(null)}
                className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DynamicImageTemplates;
